import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Info, CalendarCheck, Route, Flame, ChevronRight, TrendingUp } from 'lucide-react';
import { activeWeeks } from '../utils/trendWeeks';

const BLUE = '#0A84FF';

function useCountUp(target, active, duration = 1400) {
  const [v, setV] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    const to = Number(target) || 0;
    if (!active) { setV(to); return; }
    let start = null;
    const tick = (now) => {
      if (start === null) start = now;
      const t = Math.min(1, (now - start) / duration);
      setV(Math.round(to * (1 - Math.pow(1 - t, 3))));
      if (t < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => raf.current && cancelAnimationFrame(raf.current);
  }, [target, active, duration]);
  return v;
}

function smoothPath(pts) {
  if (pts.length < 2) return '';
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const mx = (pts[i].x + pts[i + 1].x) / 2;
    const my = (pts[i].y + pts[i + 1].y) / 2;
    d += ` Q ${pts[i].x} ${pts[i].y} ${mx} ${my}`;
  }
  const last = pts[pts.length - 1];
  d += ` Q ${last.x} ${last.y} ${last.x} ${last.y}`;
  return d;
}

const MiniStat = ({ icon: Icon, value, label, sub, tint, testId }) => (
  <div
    className={`relative rounded-2xl border px-3 py-3 ${tint
      ? 'border-[#0A84FF]/30 bg-gradient-to-b from-[#0A84FF]/[0.16] to-[#0A84FF]/[0.03]'
      : 'border-white/[0.09] bg-white/[0.04]'}`}
    data-testid={testId}
  >
    <div className="flex items-center justify-between">
      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${tint ? 'bg-[#0A84FF]/25 text-[#4FA9FF]' : 'bg-white/[0.08] text-white/65'}`}>
        <Icon size={14} />
      </span>
      <ChevronRight size={13} className="shrink-0 text-white/25" />
    </div>
    <span className="mt-1.5 block truncate font-heading text-[22px] font-black leading-none text-white tabular-nums">{value}</span>
    <p className="mt-2 text-[11px] font-medium leading-tight text-white/55">
      {label}{sub && <><br />{sub}</>}
    </p>
  </div>
);

/** Cardio week: animated six-week minutes trend plus session stats (no ring). */
export function CardioCard({ stats, isDemo }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.25 });

  const minutes = stats.week_minutes || 0;
  const trend = activeWeeks((stats.weekly_trend || []).slice(-6), 'minutes');
  const prev = [...trend.slice(0, -1)].reverse().find((t) => (t.minutes || 0) > 0)?.minutes || 0;
  const pct = prev > 0 ? Math.round(((minutes - prev) / prev) * 100) : null;
  const sessions = stats.week_sessions || 0;
  const target = stats.target_minutes || 0;

  const count = useCountUp(minutes, inView);
  const peak = Math.max(...trend.map((t) => t.minutes || 0), target, 1);

  const pts = trend.map((t, i) => ({
    x: trend.length > 1 ? 4 + (i / (trend.length - 1)) * 92 : 50,
    y: 44 - ((t.minutes || 0) / peak) * 32,
    label: t.label,
    minutes: t.minutes || 0,
    is_current: t.is_current,
  }));

  return (
    <div ref={ref} className="relative z-[1] px-4 pb-4 pt-4" data-testid="cardio-card-body">
      {/* Header */}
      <div className="flex items-start gap-3">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl p-[2px]"
              style={{ backgroundImage: 'linear-gradient(135deg,#7CD4FF 0%,#0A84FF 50%,#0B2A6B 100%)', boxShadow: '0 0 22px rgba(10,132,255,0.35)' }}>
          <img
            src="/cardio-icon.webp"
            alt=""
            width={56}
            height={56}
            loading="lazy"
            decoding="async"
            className="h-full w-full rounded-[14px] object-cover"
            data-testid="cardio-header-icon"
          />
        </span>
        <div className="min-w-0 flex-1 pt-0.5">
          <h3 className="font-heading text-[26px] font-black leading-none tracking-tight">
            <span className="text-white">Cardio </span>
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(90deg,#5AC8FA,#0A84FF)' }}>Minutes</span>
          </h3>
          <p className="mt-1.5 text-[13px] text-white/50">
            {target > 0 ? `${minutes} of ${target} minutes this week` : 'Total minutes this week'}
          </p>
        </div>
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/[0.12] bg-white/[0.05] text-white/55" data-testid="cardio-info-btn">
          <Info size={15} />
        </span>
      </div>

      {isDemo && (
        <span className="absolute right-4 top-[72px] rounded bg-[#0A84FF]/15 px-2 py-0.5 font-heading text-[10px] font-black uppercase tracking-wider text-[#4FA9FF]">Demo</span>
      )}

      {/* Headline */}
      <div className="mt-4 flex items-end gap-3" data-testid="cardio-headline">
        <span className="font-heading text-[58px] font-black leading-[0.85] text-white tabular-nums"
              style={{ textShadow: '0 2px 18px rgba(0,0,0,0.6)' }} data-testid="cardio-counter">
          {count}
        </span>
        <span className="pb-2 font-heading text-base font-bold uppercase tracking-[0.2em] text-white/40">min</span>
        {pct !== null && (
          <span className="mb-2 ml-auto flex items-center gap-1.5 rounded-full border border-white/[0.08] bg-black/70 px-3 py-1.5 backdrop-blur-sm"
                data-testid="cardio-delta">
            <TrendingUp size={13} className={pct >= 0 ? 'text-[#4FA9FF]' : 'rotate-90 text-white/45'} strokeWidth={3} />
            <span className={`font-heading text-sm font-black ${pct >= 0 ? 'text-[#4FA9FF]' : 'text-white/70'}`}>
              {pct >= 0 ? '+' : ''}{pct}%
            </span>
            <span className="text-[12px] text-white/45">vs last week</span>
          </span>
        )}
      </div>

      {/* Six-week trend */}
      {trend.length > 1 && (
        <div className="mt-3" data-testid="cardio-trend">
          <div className="relative h-[72px] w-full">
            <svg viewBox="0 0 100 50" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
              <defs>
                <linearGradient id="cardioLine" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#1C4F8F" />
                  <stop offset="45%" stopColor={BLUE} />
                  <stop offset="100%" stopColor="#7CD4FF" />
                </linearGradient>
                <linearGradient id="cardioFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgba(10,132,255,0.35)" />
                  <stop offset="100%" stopColor="rgba(10,132,255,0)" />
                </linearGradient>
                <clipPath id="cardioReveal">
                  <motion.rect
                    x="0" y="0" height="50"
                    initial={{ width: 0 }}
                    animate={inView ? { width: 100 } : { width: 0 }}
                    transition={{ duration: 1.7, ease: 'easeInOut' }}
                  />
                </clipPath>
              </defs>
              {pts.map((p) => (
                <line
                  key={`d-${p.label}`}
                  x1={p.x} y1={p.y} x2={p.x} y2="50"
                  stroke={p.is_current ? 'rgba(10,132,255,0.7)' : 'rgba(10,132,255,0.3)'}
                  strokeWidth="0.6" strokeDasharray="1.4 1.8" vectorEffect="non-scaling-stroke"
                />
              ))}
              <motion.path
                d={`${smoothPath(pts)} L ${pts[pts.length - 1].x} 50 L ${pts[0].x} 50 Z`}
                fill="url(#cardioFill)"
                initial={{ opacity: 0 }}
                animate={inView ? { opacity: 1 } : { opacity: 0 }}
                transition={{ delay: 1.1, duration: 0.8 }}
              />
              {/* glow that traces the exact curve: wide soft strokes under a crisp line */}
              <g clipPath="url(#cardioReveal)">
                {[
                  { w: 12, o: 0.08 },
                  { w: 8, o: 0.14 },
                  { w: 5, o: 0.22 },
                  { w: 3, o: 0.35 },
                ].map((g) => (
                  <path
                    key={g.w}
                    d={smoothPath(pts)} fill="none" stroke="#4FA9FF" strokeWidth={g.w}
                    strokeLinejoin="round" strokeLinecap="round" opacity={g.o}
                    vectorEffect="non-scaling-stroke"
                  />
                ))}
                <path
                  d={smoothPath(pts)} fill="none" stroke="url(#cardioLine)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"
                  style={{ filter: 'drop-shadow(0 0 3px rgba(10,132,255,0.95))' }}
                  vectorEffect="non-scaling-stroke"
                />
              </g>
            </svg>
            <motion.span
              className="pointer-events-none absolute bottom-0 w-11 rounded-t-xl"
              style={{
                left: `${pts[pts.length - 1].x}%`,
                top: `${(pts[pts.length - 1].y / 50) * 100}%`,
                transform: 'translateX(-50%)',
                background: 'linear-gradient(180deg, rgba(10,132,255,0.5) 0%, rgba(10,132,255,0.12) 55%, rgba(10,132,255,0) 100%)',
              }}
              initial={{ opacity: 0, scaleY: 0.3 }}
              animate={inView ? { opacity: 1, scaleY: 1 } : { opacity: 0, scaleY: 0.3 }}
              transition={{ delay: 1.2, duration: 0.6, ease: 'easeOut' }}
              data-testid="cardio-now-column"
            />
            {pts.map((p, i) => (
              <motion.span
                key={p.label}
                className={`absolute block rounded-full border-2 ${p.is_current
                  ? 'h-[14px] w-[14px] border-[#9BDBFF] bg-[#0A84FF]'
                  : 'h-[11px] w-[11px] border-[#4FA9FF] bg-[#061018]'}`}
                style={{
                  left: `${p.x}%`,
                  top: `${(p.y / 50) * 100}%`,
                  transform: 'translate(-50%, -50%)',
                  boxShadow: p.is_current ? '0 0 12px rgba(10,132,255,0.95)' : '0 0 6px rgba(10,132,255,0.45)',
                }}
                initial={{ scale: 0, opacity: 0 }}
                animate={inView ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
                transition={{ delay: 0.55 + i * 0.12, type: 'spring', stiffness: 420, damping: 18 }}
              />
            ))}
          </div>
          <div className="mt-1.5 grid" style={{ gridTemplateColumns: `repeat(${trend.length}, minmax(0,1fr))` }}>
            {trend.map((t) => (
              <div key={t.label} className="text-center">
                <p className={`font-heading text-[11px] font-bold uppercase tracking-wider ${t.is_current ? 'text-[#4FA9FF]' : 'text-white/40'}`}>{t.label}</p>
                <p className={`font-heading text-[15px] font-black tabular-nums ${t.is_current ? 'text-white' : 'text-white/65'}`}>{t.minutes || 0}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Session stats */}
      <div className="mt-4 grid grid-cols-3 gap-2.5" data-testid="cardio-stats-row">
        <MiniStat
          icon={CalendarCheck}
          value={stats.planned_sessions ? `${sessions}/${stats.planned_sessions}` : sessions}
          label="Sessions" sub="This Week" tint testId="cardio-stat-sessions"
        />
        <MiniStat icon={Route} value={stats.week_distance_km ? `${stats.week_distance_km}` : '0'} label="Distance" sub="km" testId="cardio-stat-distance" />
        <MiniStat icon={Flame} value={stats.week_calories || 0} label="Calories" sub="Burned" tint testId="cardio-stat-calories" />
      </div>

      <p className="mt-3.5 flex items-center justify-center gap-1.5 text-center text-[11px] text-white/35" data-testid="cardio-info-note">
        <Info size={12} className="shrink-0" />
        {stats.streak_weeks > 0
          ? `${stats.streak_weeks} week cardio streak — keep it rolling.`
          : 'Minutes counted from completed cardio blocks this week.'}
      </p>
    </div>
  );
}
